
import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Users, Lock, Unlock, UserPlus, Trash2, ShieldCheck, AlertCircle, Info, Plus, ExternalLink, Link as LinkIcon, Edit2, Check, X, ChevronLeft, Home, LogOut, MessageSquare, Star, EyeOff } from 'lucide-react';
import API_URL from '../config';
import LoadingScreen from '../components/LoadingScreen';
import './Home.css';
import './Profile.css';

export default function TeamRoom() {
    const { teamId, taskId } = useParams();
    const navigate = useNavigate();
    const location = useLocation();
    const isAdminView = location.pathname.startsWith('/admin');
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");
    const [searchEmail, setSearchEmail] = useState("");
    const [inviteResult, setInviteResult] = useState(null);
    const [availableUsers, setAvailableUsers] = useState([]);
    const [showSuggestions, setShowSuggestions] = useState(false);
    const [whatsappLink, setWhatsappLink] = useState("");
    const [savingLink, setSavingLink] = useState(false);
    const [problemStatements, setProblemStatements] = useState([""]);
    const [selectedPsIndex, setSelectedPsIndex] = useState(null);
    const [savingPS, setSavingPS] = useState(false);
    const [pptLinks, setPptLinks] = useState([""]);
    const [savingPPT, setSavingPPT] = useState(false);

    const [formTitle, setFormTitle] = useState("");
    const [formLink, setFormLink] = useState("");
    const [savingForm, setSavingForm] = useState(false);

    const [isEditingName, setIsEditingName] = useState(false);
    const [tempName, setTempName] = useState("");

    // Faculty Reviews state
    const [reviews, setReviews] = useState([]);
    const [reviewsLoading, setReviewsLoading] = useState(false);

    const currentUser = JSON.parse(localStorage.getItem("vjconnect_user") || "{}");

    useEffect(() => {
        // Fetch users for autocomplete
        fetch(`${API_URL}/users`)
            .then(res => res.json())
            .then(data => {
                if (Array.isArray(data)) setAvailableUsers(data);
                else if (data && data.users) setAvailableUsers(data.users);
            })
            .catch(err => console.error("Failed to load users for search", err));
    }, []);

    useEffect(() => {
        fetchTeam();
    }, [teamId]);

    useEffect(() => {
        if (teamId && currentUser.email) {
            setReviewsLoading(true);
            fetch(`${API_URL}/formation/teams/${teamId}/reviews?email=${encodeURIComponent(currentUser.email)}`)
                .then(r => r.json())
                .then(d => { if (d.reviews) setReviews(d.reviews); })
                .catch(() => {})
                .finally(() => setReviewsLoading(false));
        }
    }, [teamId, currentUser.email]);

    const fetchTeam = async () => {
        setLoading(true);
        try {
            const res = await fetch(`${API_URL}/formation/teams/${teamId}?email=${currentUser.email}`);
            if (!res.ok) throw new Error("Failed to fetch team details");
            const body = await res.json();
            setData(body);
            if (body.team?.whatsapp_link) {
                setWhatsappLink(body.team.whatsapp_link);
            }
            if (body.team?.team_name) {
                setTempName(body.team.team_name);
            }
            if (body.team?.problem_statement) {
                try {
                    const parsed = JSON.parse(body.team.problem_statement);
                    if (Array.isArray(parsed)) setProblemStatements(parsed);
                    else setProblemStatements([parsed]); // Legacy
                } catch (e) {
                    setProblemStatements([body.team.problem_statement]);
                }
            } else {
                setProblemStatements([""]);
            }
            if (body.team?.selected_ps_index !== undefined) {
                setSelectedPsIndex(body.team.selected_ps_index);
            }
            if (body.team?.ppt_links) {
                try {
                    const parsed = JSON.parse(body.team.ppt_links);
                    setPptLinks(Array.isArray(parsed) && parsed.length > 0 ? parsed : [""]);
                } catch (e) {
                    setPptLinks([""]);
                }
            }
            if (body.team?.form_title) setFormTitle(body.team.form_title);
            if (body.team?.form_link) setFormLink(body.team.form_link);
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    const isLead = useMemo(() => data?.team?.lead_email === currentUser.email, [data, currentUser.email]);
    const isMember = useMemo(() => data?.members?.some(m => m.email === currentUser.email), [data, currentUser.email]);
    const isAdmin = useMemo(() => {
        const ADMIN_EMAILS = ["24071a6691@vnrvjiet.in", "24071a6679@vnrvjiet.in"];
        const isEmailAdmin = ADMIN_EMAILS.includes((currentUser.email || "").toLowerCase().trim());
        const isAuthorizedFaculty = data?.task && (
            String(data.task.creator_email).toLowerCase().trim() === (currentUser.email || "").toLowerCase().trim() ||
            (data.task.collaborators && data.task.collaborators.some(c => String(c.email).toLowerCase().trim() === (currentUser.email || "").toLowerCase().trim()))
        );
        return isEmailAdmin || isAdminView || isAuthorizedFaculty;
    }, [currentUser.email, isAdminView, data?.task]);

    const rules = useMemo(() => JSON.parse(data?.task?.rules || '{}'), [data]);

    const ruleStatus = useMemo(() => {
        if (!data) return null;
        const { members, task } = data;

        // Section Counts
        const sectionCounts = members.reduce((acc, m) => {
            acc[m.department] = (acc[m.department] || 0) + 1;
            return acc;
        }, {});

        // Gender Counts
        const femaleCount = members.filter(m => String(m.gender).toLowerCase() === 'female').length;

        return { sectionCounts, femaleCount };
    }, [data]);

    const handleJoin = async () => {
        try {
            const res = await fetch(`${API_URL}/formation/teams/join`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ team_id: teamId, user_email: currentUser.email })
            });
            if (res.ok) {
                fetchTeam();
            } else {
                const err = await res.json();
                alert(err.error || "Failed to join");
            }
        } catch (err) {
            console.error(err);
        }
    };

    const handleLock = async () => {
        if (!window.confirm("Once locked, no further changes can be made. Lock this team?")) return;
        try {
            const res = await fetch(`${API_URL}/formation/teams/lock`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ team_id: teamId, lead_email: currentUser.email })
            });
            if (res.ok) {
                fetchTeam();
            } else {
                const err = await res.json();
                alert(err.error || "Failed to lock");
            }
        } catch (err) {
            console.error(err);
        }
    };

    const handleRemove = async (email) => {
        if (!window.confirm(`Remove ${email} from the team?`)) return;
        try {
            const res = await fetch(`${API_URL}/formation/teams/remove-member`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ team_id: teamId, lead_email: currentUser.email, target_email: email })
            });
            if (res.ok) fetchTeam();
        } catch (err) {
            console.error(err);
        }
    };

    const handleInvite = async () => {
        if (!searchEmail.trim()) return;
        try {
            const res = await fetch(`${API_URL}/formation/invitations/send`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ team_id: teamId, inviter_email: currentUser.email, invitee_email: searchEmail })
            });
            if (res.ok) {
                alert("Invitation sent!");
                setSearchEmail("");
            } else {
                const err = await res.json();
                alert(err.error || "Failed to invite");
            }
        } catch (err) {
            console.error(err);
        }
    };

    const handleSaveWhatsapp = async () => {
        setSavingLink(true);
        try {
            const res = await fetch(`${API_URL}/formation/teams/${teamId}/whatsapp`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    lead_email: currentUser.email,
                    whatsapp_link: whatsappLink
                })
            });
            if (res.ok) {
                alert("WhatsApp link saved!");
                fetchTeam();
            } else {
                const err = await res.json();
                alert(err.error || "Failed to save link");
            }
        } catch (err) {
            console.error(err);
        } finally {
            setSavingLink(false);
        }
    };

    const handleSaveForm = async () => {
        setSavingForm(true);
        try {
            const res = await fetch(`${API_URL}/formation/teams/${teamId}/form`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    lead_email: currentUser.email,
                    form_title: formTitle,
                    form_link: formLink
                })
            });
            if (res.ok) {
                alert("Form details saved!");
                fetchTeam();
            } else {
                const err = await res.json();
                alert(err.error || "Failed to save form details");
            }
        } catch (err) {
            console.error(err);
        } finally {
            setSavingForm(false);
        }
    };

    const handleSavePS = async () => {
        setSavingPS(true);
        try {
            const filtered = problemStatements.filter(ps => ps.trim() !== "");
            const res = await fetch(`${API_URL}/formation/teams/${teamId}/problem-statement`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    lead_email: currentUser.email,
                    problem_statements: filtered
                })
            });
            if (res.ok) {
                alert("Problem Statements saved!");
                fetchTeam();
            } else {
                const err = await res.json();
                alert(err.error || "Failed to save");
            }
        } catch (err) {
            console.error(err);
        } finally {
            setSavingPS(false);
        }
    };

    const handleAddPS = () => {
        if (problemStatements.length >= 5) return;
        setProblemStatements([...problemStatements, ""]);
    };
    const handlePSChange = (idx, val) => {
        const newPS = [...problemStatements];
        newPS[idx] = val;
        setProblemStatements(newPS);
    };
    const handleRemovePS = (idx) => {
        if (problemStatements.length === 1) {
            setProblemStatements([""]);
        } else {
            setProblemStatements(problemStatements.filter((_, i) => i !== idx));
        }
    };

    const handleSelectPS = async (index) => {
        if (!window.confirm("Select this problem statement? This will be highlighted in the export.")) return;
        try {
            const res = await fetch(`${API_URL}/formation/teams/${teamId}/select-problem-statement`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    email: currentUser.email,
                    index: index
                })
            });
            if (res.ok) fetchTeam();
        } catch (err) { console.error(err); }
    };

    const handleSavePPT = async () => {
        setSavingPPT(true);
        try {
            const filteredLinks = pptLinks.filter(link => link.trim() !== "");
            const res = await fetch(`${API_URL}/formation/teams/${teamId}/ppt-links`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    lead_email: currentUser.email,
                    ppt_links: filteredLinks
                })
            });
            if (res.ok) {
                alert("PPT Links saved!");
                fetchTeam();
            } else {
                const err = await res.json();
                alert(err.error || "Failed to save PPT links");
            }
        } catch (err) {
            console.error(err);
        } finally {
            setSavingPPT(false);
        }
    };

    const handleAddPPTLink = () => setPptLinks([...pptLinks, ""]);
    const handlePPTLinkChange = (idx, val) => {
        const newLinks = [...pptLinks];
        newLinks[idx] = val;
        setPptLinks(newLinks);
    };
    const handleRemovePPTLink = (idx) => {
        if (pptLinks.length === 1) {
            setPptLinks([""]);
        } else {
            setPptLinks(pptLinks.filter((_, i) => i !== idx));
        }
    };

    const handleJoinWhatsapp = () => {
        window.open(`${API_URL}/formation/teams/${teamId}/join-whatsapp?email=${currentUser.email}`, '_blank');
    };

    const handleDeleteTeam = async () => {
        if (!window.confirm(`Are you sure you want to delete "${data.team.team_name}"? This action cannot be undone.`)) {
            return;
        }

        try {
            const res = await fetch(`${API_URL}/formation/teams/${teamId}`, {
                method: 'DELETE',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ user_email: currentUser.email })
            });

            if (res.ok) {
                alert("Team deleted successfully!");
                navigate(`/formation/${data.team.task_id}`); // Navigate back to task page
            } else {
                const err = await res.json();
                alert(err.error || "Failed to delete team");
            }
        } catch (err) {
            console.error("Delete error:", err);
            alert("Failed to delete team");
        }
    };

    const handleRename = async () => {
        if (!tempName.trim()) return;
        if (tempName.trim() === team.team_name) {
            setIsEditingName(false);
            return;
        }
        try {
            const res = await fetch(`${API_URL}/formation/teams/${teamId}/rename`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    lead_email: currentUser.email,
                    team_name: tempName.trim()
                })
            });
            if (res.ok) {
                setIsEditingName(false);
                fetchTeam();
            } else {
                const err = await res.json();
                alert(err.error || "Failed to rename team");
            }
        } catch (err) {
            console.error(err);
        }
    };

    if (loading) return <LoadingScreen message="Team Room" />;
    if (error) return <div className="error-screen">{error}</div>;

    const { team, task, members } = data;

    const isExcelRoom = (() => {
        if (!task) return false;
        let rules = {};
        let visRules = {};
        try { rules = typeof task.rules === 'string' ? JSON.parse(task.rules) : (task.rules || {}); } catch(e){}
        try { visRules = typeof task.visibility_rules === 'string' ? JSON.parse(task.visibility_rules) : (task.visibility_rules || {}); } catch(e){}
        return !!(rules.excel_created || visRules.creation_mode === 'excel' || (visRules.restricted_roll_nos && visRules.restricted_roll_nos.length > 0));
    })();
    const isFixedRoom = isExcelRoom || team.is_auto_generated || task.status === 'locked';

    return (
        <div className="home-page-container">
            <div className="home-content-wrapper">

                {/* Header Card */}
                <div className="glass-card header-card-main" style={{ padding: '1.25rem 2rem', marginBottom: '2rem', borderRadius: '24px' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '1.25rem' }}>

                        {/* Left Section: Back Button + Team Name & Task Info */}
                        <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', flexWrap: 'wrap' }}>
                            {isAdminView ? (
                                <button
                                    onClick={() => (task?.id || team?.task_id) ? navigate(`/admin/teams/${task?.id || team?.task_id}`) : navigate(-1)}
                                    title="Back to Teams"
                                    style={{
                                        width: 42, height: 42, borderRadius: '12px',
                                        border: '1px solid var(--glass-border)', background: 'var(--glass-bg)',
                                        color: 'var(--text-main)', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
                                        boxShadow: '0 2px 8px rgba(0,0,0,0.1)', flexShrink: 0
                                    }}
                                >
                                    <ChevronLeft size={20} />
                                </button>
                            ) : (
                                <button
                                    onClick={() => (task?.id || team?.task_id) ? navigate(`/formation/${task?.id || team?.task_id}`) : navigate(-1)}
                                    title="Back to Task"
                                    style={{
                                        width: 42, height: 42, borderRadius: '12px',
                                        border: '1px solid var(--glass-border)', background: 'var(--glass-bg)',
                                        color: 'var(--text-main)', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
                                        boxShadow: '0 2px 8px rgba(0,0,0,0.1)', flexShrink: 0
                                    }}
                                >
                                    <ChevronLeft size={20} />
                                </button>
                            )}

                            <div style={{ textAlign: 'left' }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                    <span style={{ fontSize: '0.75rem', opacity: 0.5, textTransform: 'uppercase', letterSpacing: '1px', fontWeight: 'bold' }}>TEAM #{team.team_number}</span>
                                    {team.status === 'locked' && (
                                        <span style={{ fontSize: '0.75rem', background: 'rgba(239, 68, 68, 0.15)', color: '#ef4444', border: '1px solid rgba(239, 68, 68, 0.3)', padding: '2px 8px', borderRadius: '12px', fontWeight: 'bold', display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
                                            <Lock size={12} /> Locked
                                        </span>
                                    )}
                                </div>
                                {isEditingName ? (
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginTop: '4px' }}>
                                        <input
                                            className="profile-input"
                                            style={{ fontSize: '1.25rem', fontWeight: 'bold', padding: '4px 10px', marginBottom: 0 }}
                                            value={tempName}
                                            onChange={(e) => setTempName(e.target.value)}
                                            autoFocus
                                        />
                                        <button onClick={handleRename} className="btn-profile btn-profile-primary" style={{ padding: '6px' }} title="Save">
                                            <Check size={16} />
                                        </button>
                                        <button onClick={() => { setIsEditingName(false); setTempName(team.team_name); }} className="btn-profile btn-profile-error" style={{ padding: '6px' }} title="Cancel">
                                            <X size={16} />
                                        </button>
                                    </div>
                                ) : (
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginTop: '2px' }}>
                                        <h1 style={{ fontSize: '1.75rem', fontWeight: 'bold', margin: 0, color: 'var(--text-main)' }}>{team.team_name}</h1>
                                        {isLead && (
                                            <button
                                                onClick={() => { setIsEditingName(true); setTempName(team.team_name); }}
                                                className="btn-profile btn-profile-secondary"
                                                style={{ padding: '4px', borderRadius: '50%', minWidth: '28px', height: '28px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                                                title="Edit Team Name"
                                            >
                                                <Edit2 size={14} />
                                            </button>
                                        )}
                                    </div>
                                )}
                                <p style={{ opacity: 0.6, fontSize: '0.85rem', margin: '2px 0 0 0' }}>Task: <strong>{task.name}</strong></p>
                            </div>
                        </div>

                        {/* Right Section: Stats & Navigation */}
                        <div style={{ display: 'flex', alignItems: 'center', gap: '1.5rem', flexWrap: 'wrap' }}>
                            <div style={{ textAlign: 'center', padding: '0 1rem', borderLeft: '1px solid var(--glass-border)', borderRight: '1px solid var(--glass-border)' }}>
                                <div style={{ fontSize: '1.5rem', fontWeight: 'bold', color: 'var(--accent-color)' }}>
                                    {isFixedRoom ? members.length : `${members.length}/${task.max_members}`}
                                </div>
                                <div style={{ fontSize: '0.75rem', opacity: 0.6 }}>Members</div>
                            </div>

                            <div style={{ textAlign: 'center', paddingRight: '1rem' }}>
                                <div style={{ fontSize: '1rem', fontWeight: '600', color: 'var(--text-main)' }}>
                                    {new Date(team.created_at).toLocaleDateString()}
                                </div>
                                <div style={{ fontSize: '0.75rem', opacity: 0.6 }}>Created</div>
                            </div>

                            <div style={{ display: 'flex', gap: '0.5rem' }}>
                                <button
                                    onClick={() => navigate(currentUser.role === 'faculty' ? '/faculty/home' : '/home')}
                                    title="Home"
                                    style={{
                                        width: 40, height: 40, borderRadius: '12px',
                                        border: '1px solid var(--glass-border)', background: 'var(--glass-bg)',
                                        color: 'var(--text-main)', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
                                        boxShadow: '0 2px 8px rgba(0,0,0,0.1)', flexShrink: 0
                                    }}
                                >
                                    <Home size={18} />
                                </button>
                                <button
                                    onClick={() => { localStorage.removeItem('vjconnect_user'); navigate('/'); }}
                                    title="Logout"
                                    style={{
                                        width: 40, height: 40, borderRadius: '12px',
                                        border: 'none', background: '#ef4444',
                                        color: '#ffffff', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
                                        boxShadow: '0 2px 8px rgba(239,68,68,0.4)', flexShrink: 0
                                    }}
                                >
                                    <LogOut size={18} />
                                </button>
                            </div>
                        </div>

                    </div>
                </div>

                <div className="team-room-grid">
                    {/* Left Column: PS and PPT */}
                    <div className="team-room-main-content" style={{ display: 'flex', flexDirection: 'column', gap: '2rem' }}>
                        {/* Problem Statement Block */}
                        <div className="glass-card" style={{ padding: '2rem', borderRadius: '30px' }}>
                            <h3 className="section-title" style={{ marginBottom: '1.5rem', display: 'flex', alignItems: 'center', gap: '10px', color: '#ec4899' }}>
                                <AlertCircle size={24} /> Problem Statement
                            </h3>
                            {isMember ? (
                                <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                                    {problemStatements.map((ps, idx) => (
                                        <div key={idx} style={{ display: 'flex', gap: '10px', alignItems: 'flex-start' }}>
                                            <div style={{ paddingTop: '10px', fontWeight: 'bold', minWidth: '20px' }}>{idx + 1}.</div>
                                            <div style={{ flex: 1 }}>
                                                <textarea
                                                    className="profile-input"
                                                    style={{
                                                        minHeight: '80px',
                                                        resize: 'vertical',
                                                        borderColor: selectedPsIndex === idx ? '#22c55e' : '',
                                                        borderWidth: selectedPsIndex === idx ? '2px' : '1px'
                                                    }}
                                                    placeholder={`Problem Statement ${idx + 1}`}
                                                    value={ps}
                                                    onChange={e => handlePSChange(idx, e.target.value)}
                                                />
                                                {selectedPsIndex === idx && <div style={{ color: '#22c55e', fontSize: '0.8rem', marginTop: '4px' }}>✓ Selected by Mentor-start working on it!</div>}
                                            </div>
                                            <button
                                                onClick={() => handleRemovePS(idx)}
                                                className="btn-profile btn-profile-error"
                                                style={{ padding: '10px', minWidth: '40px', marginTop: '4px' }}
                                                title="Remove"
                                            >
                                                <Trash2 size={18} />
                                            </button>
                                            {isAdmin && (
                                                <button
                                                    onClick={() => handleSelectPS(idx)}
                                                    className="btn-profile"
                                                    style={{
                                                        padding: '10px',
                                                        minWidth: '40px',
                                                        marginTop: '4px',
                                                        background: selectedPsIndex === idx ? '#22c55e' : 'rgba(255,255,255,0.1)',
                                                        color: 'white'
                                                    }}
                                                    title="Select this statement"
                                                >
                                                    <ShieldCheck size={18} />
                                                </button>
                                            )}
                                        </div>
                                    ))}

                                    <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '10px' }}>
                                        <button
                                            onClick={handleAddPS}
                                            className="btn-profile btn-profile-secondary"
                                            style={{ display: 'flex', alignItems: 'center', gap: '8px' }}
                                            disabled={problemStatements.length >= 5}
                                        >
                                            <Plus size={18} /> Add More ({problemStatements.length}/5)
                                        </button>
                                        <button
                                            onClick={handleSavePS}
                                            className="btn-profile btn-profile-primary"
                                            disabled={savingPS}
                                            style={{ padding: '10px 24px' }}
                                        >
                                            {savingPS ? "Saving..." : "Save All"}
                                        </button>
                                    </div>
                                </div>
                            ) : (
                                <div style={{
                                    background: 'rgba(255,255,255,0.05)',
                                    padding: '1.5rem',
                                    borderRadius: '15px',
                                    border: '1px solid var(--glass-border)',
                                    minHeight: '100px'
                                }}>
                                    {(team.problem_statement || (isMember || isAdmin ? "No statements." : "Restricted.")) ? (
                                        <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                                            {problemStatements.filter(p => p.trim()).length > 0 ? (
                                                problemStatements.filter(p => p.trim()).map((ps, idx) => (
                                                    <div key={idx} className={`problem-statement-item ${selectedPsIndex === idx ? 'selected' : ''}`}>
                                                        {selectedPsIndex === idx && <div style={{ position: 'absolute', top: -10, right: 10, background: '#22c55e', color: 'white', fontSize: '0.7rem', padding: '2px 8px', borderRadius: '10px' }}>SELECTED BY MENTOR</div>}
                                                        <div style={{ fontWeight: 'bold', marginBottom: '0.5rem', opacity: 0.5 }}>Statement {idx + 1}</div>
                                                        <p style={{ margin: 0, whiteSpace: 'pre-wrap' }}>{ps}</p>
                                                        {isAdmin && (
                                                            <button
                                                                onClick={() => handleSelectPS(idx)}
                                                                className={`btn-profile ${selectedPsIndex === idx ? '' : 'btn-profile-secondary'}`}
                                                                style={{
                                                                    marginTop: '10px',
                                                                    padding: '4px 12px',
                                                                    fontSize: '0.8rem',
                                                                    width: '100%',
                                                                    ...(selectedPsIndex === idx ? { background: '#22c55e', color: 'white' } : {})
                                                                }}
                                                            >
                                                                {selectedPsIndex === idx ? "Selected" : "Select this statement"}
                                                            </button>
                                                        )}
                                                    </div>
                                                ))
                                            ) : (
                                                <p>No problem statements added yet.</p>
                                            )}
                                        </div>
                                    ) : (
                                        <div style={{ textAlign: 'center', opacity: 0.5 }}>
                                            <Info size={40} style={{ marginBottom: '10px' }} />
                                            <p>Problem statement will appear here once added.</p>
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>

                        {/* PPT / PDF Links Block */}
                        <div className="glass-card" style={{ padding: '2rem', borderRadius: '30px' }}>
                            <h3 className="section-title" style={{ marginBottom: '1.5rem', display: 'flex', alignItems: 'center', gap: '10px', color: '#3b82f6' }}>
                                <LinkIcon size={24} /> Share The DOC/PPT Links
                            </h3>
                            {isMember ? (
                                <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                                    {pptLinks.map((link, idx) => (
                                        <div key={idx} style={{ display: 'flex', gap: '10px' }}>
                                            <div style={{ position: 'relative', flex: 1 }}>
                                                <input
                                                    type="text"
                                                    className="profile-input"
                                                    placeholder="Paste PPT/PDF Drive Link"
                                                    value={link}
                                                    onChange={e => handlePPTLinkChange(idx, e.target.value)}
                                                />
                                            </div>
                                            <button
                                                onClick={() => handleRemovePPTLink(idx)}
                                                className="btn-profile btn-profile-error"
                                                style={{ padding: '10px', minWidth: '40px' }}
                                            >
                                                <Trash2 size={18} />
                                            </button>
                                        </div>
                                    ))}
                                    <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '10px' }}>
                                        <button
                                            onClick={handleAddPPTLink}
                                            className="btn-profile btn-profile-secondary"
                                            style={{ display: 'flex', alignItems: 'center', gap: '8px' }}
                                        >
                                            <Plus size={18} /> Add More
                                        </button>
                                        <button
                                            onClick={handleSavePPT}
                                            className="btn-profile btn-profile-primary"
                                            disabled={savingPPT}
                                            style={{ padding: '10px 24px' }}
                                        >
                                            {savingPPT ? "Saving..." : "Save Links"}
                                        </button>
                                    </div>
                                </div>
                            ) : (
                                <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                                    {isMember || isAdmin || team.ppt_links ? (
                                        (() => {
                                            try {
                                                const links = JSON.parse(team.ppt_links || '[]');
                                                if (links.length === 0) return <p style={{ opacity: 0.5, textAlign: 'center' }}>No links shared yet.</p>;
                                                return links.map((link, idx) => (
                                                    <a
                                                        key={idx}
                                                        href={link.startsWith('http') ? link : `https://${link}`}
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                        className="glass-card"
                                                        style={{
                                                            padding: '12px 20px',
                                                            borderRadius: '12px',
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            gap: '12px',
                                                            textDecoration: 'none',
                                                            color: 'inherit',
                                                            border: '1px solid var(--glass-border)',
                                                            background: 'rgba(255,255,255,0.05)',
                                                            transition: 'all 0.2s'
                                                        }}
                                                        onMouseOver={e => e.currentTarget.style.background = 'rgba(255,255,255,0.1)'}
                                                        onMouseOut={e => e.currentTarget.style.background = 'rgba(255,255,255,0.05)'}
                                                    >
                                                        <ExternalLink size={18} style={{ color: '#3b82f6' }} />
                                                        <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{link}</span>
                                                    </a>
                                                ));
                                            } catch (e) {
                                                return <p style={{ opacity: 0.5, textAlign: 'center' }}>Error loading links.</p>;
                                            }
                                        })()
                                    ) : (
                                        <p style={{ opacity: 0.5, textAlign: 'center' }}>Content restricted.</p>
                                    )}
                                </div>
                            )}
                        </div>

                        {/* Faculty Reviews Block */}
                        <div className="glass-card" style={{ padding: '2rem', borderRadius: '30px' }}>
                            <h3 className="section-title" style={{ marginBottom: '1.5rem', display: 'flex', alignItems: 'center', gap: '10px', color: '#6366f1' }}>
                                <MessageSquare size={24} /> Faculty Reviews
                            </h3>
                            {reviewsLoading ? (
                                <div style={{ textAlign: 'center', padding: '2rem', opacity: 0.5 }}>Loading reviews...</div>
                            ) : reviews.length === 0 ? (
                                <div style={{ textAlign: 'center', padding: '2rem', opacity: 0.4 }}>
                                    <MessageSquare size={36} style={{ marginBottom: '0.75rem', display: 'block', margin: '0 auto 10px' }} />
                                    <p style={{ margin: 0 }}>No faculty reviews yet.</p>
                                </div>
                            ) : (
                                <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                                    {reviews.map((rev, idx) => (
                                        <div key={rev.id || idx} style={{
                                            background: 'rgba(99,102,241,0.06)', border: '1px solid rgba(99,102,241,0.2)',
                                            borderRadius: '16px', padding: '1.2rem 1.4rem'
                                        }}>
                                            <div style={{ display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: '8px', marginBottom: '0.75rem' }}>
                                                <div style={{ fontWeight: 700, fontSize: '0.95rem', color: '#818cf8' }}>{rev.session_name || 'Review'}</div>
                                                <div style={{ fontSize: '0.75rem', opacity: 0.5 }}>{rev.review_date} · {rev.reviewer_name || rev.reviewer_email}</div>
                                            </div>
                                            {/* Marks — only visible to coordinators/reviewers */}
                                            {rev.marks != null && (
                                                <div style={{
                                                    display: 'inline-flex', alignItems: 'center', gap: '5px',
                                                    background: 'rgba(99,102,241,0.15)', color: '#818cf8',
                                                    padding: '3px 10px', borderRadius: '20px', fontSize: '0.8rem', fontWeight: 700, marginBottom: '10px'
                                                }}>
                                                    <Star size={12} /> {rev.marks} marks
                                                </div>
                                            )}
                                            {/* Private feedback — only shown to coordinators/reviewers */}
                                            {rev.private_feedback && (
                                                <div style={{
                                                    background: 'rgba(245,158,11,0.07)', border: '1px solid rgba(245,158,11,0.2)',
                                                    borderRadius: '10px', padding: '10px 14px', marginBottom: '10px'
                                                }}>
                                                    <div style={{ fontSize: '0.72rem', fontWeight: 700, color: '#f59e0b', marginBottom: '5px', display: 'flex', alignItems: 'center', gap: 4 }}>
                                                        <EyeOff size={11} /> Internal Note (Coordinator Only)
                                                    </div>
                                                    <div style={{ fontSize: '0.88rem', whiteSpace: 'pre-wrap', opacity: 0.85 }}>{rev.private_feedback}</div>
                                                </div>
                                            )}
                                            {/* Student feedback — always visible */}
                                            {rev.student_feedback ? (
                                                <div style={{ fontSize: '0.9rem', whiteSpace: 'pre-wrap', lineHeight: 1.6 }}>{rev.student_feedback}</div>
                                            ) : (
                                                <div style={{ opacity: 0.4, fontSize: '0.85rem', fontStyle: 'italic' }}>No student feedback shared yet.</div>
                                            )}
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>

                        {/* Members Listing */}
                        <div className="glass-card" style={{ padding: '2rem', borderRadius: '30px' }}>
                            <h3 className="section-title" style={{ marginBottom: '2rem' }}>
                                <Users size={20} style={{ marginRight: 10 }} /> Team Members ({isFixedRoom ? members.length : `${members.length}/${task.max_members}`})
                            </h3>

                            <div className="team-members-grid">
                                {members.map(member => (
                                    <div key={member.email} className="member-info-card" style={{ border: member.role === 'lead' ? '2px solid var(--accent-color)' : '1px solid var(--glass-border)', position: 'relative' }}>
                                        <div className="member-avatar-box">
                                            {member.profile_pic ? (
                                                <img src={member.profile_pic} alt="" />
                                            ) : (
                                                <div style={{
                                                    width: '100%', height: '100%', borderRadius: '50%',
                                                    background: member.is_dummy ? 'linear-gradient(135deg, #64748b, #475569)' : 'var(--primary)',
                                                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                                                    fontSize: '1.5rem', fontWeight: 'bold', color: 'white'
                                                }}>
                                                    {member.name?.charAt(0) || '?'}
                                                </div>
                                            )}
                                        </div>
                                        <div
                                            style={{ flex: 1, cursor: member.is_dummy ? 'default' : 'pointer' }}
                                            onClick={() => { if (!member.is_dummy) window.location.href = `/profile?email=${member.email}`; }}
                                        >
                                            <div style={{ fontWeight: 'bold', fontSize: '1.1rem', marginBottom: '0.2rem', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '4px' }}>
                                                <span style={{ textDecoration: member.is_dummy ? 'none' : 'underline', textDecorationColor: 'transparent', transition: 'all 0.2s' }} className={member.is_dummy ? '' : 'hover-underline'}>
                                                    {member.name || '(Unknown)'}
                                                </span>
                                                <div style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
                                                    {member.role === 'lead' && <span className="lead-badge" style={{ margin: 0 }}>LEAD</span>}
                                                    {member.is_dummy === 1 && (
                                                        <span style={{
                                                            fontSize: '0.65rem', fontWeight: '700', padding: '2px 8px',
                                                            borderRadius: '20px', background: 'rgba(251,191,36,0.15)',
                                                            border: '1px solid rgba(251,191,36,0.4)', color: '#fbbf24',
                                                            letterSpacing: '0.03em', whiteSpace: 'nowrap'
                                                        }}>
                                                            Not Registered Yet
                                                        </span>
                                                    )}
                                                </div>
                                            </div>
                                            <div style={{ opacity: 0.6, fontSize: '0.9rem' }}>{member.rollNo || '—'}</div>
                                            <div style={{ opacity: 0.6, fontSize: '0.9rem' }}>{member.department || (member.is_dummy ? 'Not yet registered' : '')}</div>
                                            <div style={{ opacity: 0.4, fontSize: '0.8rem', marginTop: '4px' }}>{member.gender || ''}</div>
                                        </div>
                                        {isLead && member.email !== currentUser.email && team.status !== 'locked' && (
                                            <button onClick={() => handleRemove(member.email)} className="btn-profile btn-profile-error" style={{ padding: '6px', position: 'absolute', top: '10px', right: '10px', borderRadius: '50%' }}>
                                                <Trash2 size={14} />
                                            </button>
                                        )}
                                    </div>
                                ))}

                                {/* Empty Slots */}
                                {(() => {
                                    if (isFixedRoom) return null;
                                    const maxMembers = Number(task.max_members) || 0;
                                    const memberCount = Array.isArray(members) ? members.length : 0;
                                    const emptySlots = Math.max(0, maxMembers - memberCount);
                                    return Array(emptySlots).fill(0).map((_, i) => (
                                        <div key={`empty-${i}`} className="member-info-card" style={{ border: '2px dashed var(--glass-border)', background: 'transparent', justifyContent: 'center', minHeight: '120px' }}>
                                            <span style={{ opacity: 0.3 }}>Empty Slot</span>
                                        </div>
                                    ));
                                })()}
                            </div>

                            {/* Actions for Non-Members */}
                            {!isMember && team.status !== 'locked' && (
                                <div style={{ marginTop: '2rem', textAlign: 'center' }}>
                                    <button onClick={handleJoin} className="btn-profile btn-profile-primary" style={{ padding: '12px 32px', fontSize: '1.1rem' }}>
                                        <Plus size={20} style={{ marginRight: 8 }} /> Join this Team
                                    </button>
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Sidebar: Rules & Management */}
                    <div className="team-room-sidebar">

                        {/* Rule Status */}
                        {!isFixedRoom && (
                            <div className="glass-card" style={{ padding: '2rem', borderRadius: '24px' }}>
                                <h3 className="section-title" style={{ fontSize: '1.2rem', marginBottom: '1.5rem' }}>
                                    <ShieldCheck size={20} style={{ marginRight: 8 }} /> Rules Status
                                </h3>
                                <div style={{ marginTop: '1rem', display: 'flex', flexDirection: 'column', gap: '1rem' }}>

                                    {/* Gender Rule */}
                                    {rules.genderRequirements?.female && (
                                        <div className={`rule-item ${ruleStatus.femaleCount >= rules.genderRequirements.female ? 'success' : 'pending'}`}>
                                            <div className="rule-label">Female Members: {ruleStatus.femaleCount} / {rules.genderRequirements.female}</div>
                                            <div className="rule-progress">
                                                <div className="rule-progress-bar" style={{ width: `${Math.min(100, (ruleStatus.femaleCount / rules.genderRequirements.female) * 100)}%` }} />
                                            </div>
                                        </div>
                                    )}

                                    {/* Section Rules */}
                                    {rules.sectionLimits && Object.entries(rules.sectionLimits).map(([dept, max]) => (
                                        <div key={dept} className={`rule-item ${ruleStatus.sectionCounts[dept] === max ? 'full' : 'ok'}`}>
                                            <div className="rule-label">{dept}: {ruleStatus.sectionCounts[dept] || 0} / {max}</div>
                                        </div>
                                    ))}
                                </div>

                                <div style={{ marginTop: '1.5rem', fontSize: '0.8rem', color: 'rgba(255,255,255,0.4)', background: 'rgba(255,255,255,0.05)', padding: '0.8rem', borderRadius: '8px' }}>
                                    <Info size={14} style={{ marginRight: 6, verticalAlign: 'middle' }} />
                                    Rules are automatically enforced when joining or locking.
                                </div>
                            </div>
                        )}

                        {/* WhatsApp Group Section - ONLY SHOWN WHEN LOCKED */}
                        {isMember && team.status === 'locked' && (
                            <div className="glass-card" style={{ padding: '1.5rem' }}>
                                <h3 className="profile-card-header" style={{ fontSize: '1.1rem' }}>
                                    <LinkIcon size={18} style={{ marginRight: 8 }} /> WhatsApp Group
                                </h3>
                                <div style={{ marginTop: '1rem', display: 'flex', flexDirection: 'column', gap: '0.8rem' }}>
                                    {isLead ? (
                                        <>
                                            <input
                                                type="text"
                                                className="profile-input"
                                                placeholder="Paste WhatsApp Group Link"
                                                value={whatsappLink}
                                                onChange={e => setWhatsappLink(e.target.value)}
                                            />
                                            <button
                                                onClick={handleSaveWhatsapp}
                                                className="btn-profile btn-profile-primary"
                                                disabled={savingLink}
                                            >
                                                {savingLink ? "Saving..." : "Save Link"}
                                            </button>
                                            <p style={{ fontSize: '0.75rem', opacity: 0.5, textAlign: 'center' }}>
                                                Only your team members can see the join button.
                                            </p>
                                        </>
                                    ) : (
                                        team.whatsapp_link ? (
                                            <button
                                                onClick={handleJoinWhatsapp}
                                                className="btn-profile btn-profile-primary"
                                                style={{ background: '#25D366', color: 'white', border: 'none' }}
                                            >
                                                <ExternalLink size={18} style={{ marginRight: 8 }} /> Join Group Chat
                                            </button>
                                        ) : (
                                            <p style={{ fontSize: '0.9rem', opacity: 0.6, textAlign: 'center', padding: '1rem' }}>
                                                Team lead hasn't shared the WhatsApp link yet.
                                            </p>
                                        )
                                    )}
                                </div>
                            </div>
                        )}

                        {/* Team Google Form Section */}
                        <div className="glass-card" style={{ padding: '1.5rem', marginTop: '2rem' }}>
                            <h3 className="profile-card-header" style={{ fontSize: '1.1rem' }}>
                                <ExternalLink size={18} style={{ marginRight: 8 }} /> Team Form
                            </h3>
                            <div style={{ marginTop: '1rem', display: 'flex', flexDirection: 'column', gap: '0.8rem' }}>
                                {isLead ? (
                                    <>
                                        <input
                                            type="text"
                                            className="profile-input"
                                            placeholder="Form Title (e.g. Feedback Form)"
                                            value={formTitle}
                                            onChange={e => setFormTitle(e.target.value)}
                                        />
                                        <input
                                            type="text"
                                            className="profile-input"
                                            placeholder="Paste Form Link"
                                            value={formLink}
                                            onChange={e => setFormLink(e.target.value)}
                                        />
                                        <button
                                            onClick={handleSaveForm}
                                            className="btn-profile btn-profile-primary"
                                            disabled={savingForm || (!formTitle && !formLink)}
                                        >
                                            {savingForm ? "Saving..." : "Save Form"}
                                        </button>
                                        <p style={{ fontSize: '0.75rem', opacity: 0.5, textAlign: 'center' }}>
                                            This form will be visible to everyone in the formation room.
                                        </p>
                                    </>
                                ) : (
                                    team.form_link ? (
                                        <button
                                            onClick={() => window.open(team.form_link.startsWith('http') ? team.form_link : `https://${team.form_link}`, '_blank')}
                                            className="btn-profile btn-profile-primary"
                                        >
                                            <ExternalLink size={18} style={{ marginRight: 8 }} /> {team.form_title || 'Open Form'}
                                        </button>
                                    ) : (
                                        <p style={{ fontSize: '0.9rem', opacity: 0.6, textAlign: 'center', padding: '1rem' }}>
                                            Team lead hasn't added a form yet.
                                        </p>
                                    )
                                )}
                            </div>
                        </div>

                        {/* Inivite / Management */}
                        {isMember && team.status !== 'locked' && (
                            <div className="glass-card" style={{ padding: '1.5rem' }}>
                                <h3 className="profile-card-header" style={{ fontSize: '1.1rem', color: 'var(--text-main)' }}><UserPlus /> Invite Peer</h3>
                                <div style={{ marginTop: '1rem', display: 'flex', flexDirection: 'column', gap: '0.8rem' }}>
                                    <div style={{ position: 'relative' }}>
                                        <input
                                            type="text"
                                            className="profile-input"
                                            placeholder="Enter Email or Roll No"
                                            value={searchEmail}
                                            onChange={e => {
                                                setSearchEmail(e.target.value);
                                                setShowSuggestions(true);
                                            }}
                                            onFocus={() => setShowSuggestions(true)}
                                            onBlur={() => setTimeout(() => setShowSuggestions(false), 200)}
                                        />
                                        {showSuggestions && searchEmail.trim() && (
                                            <div className="glass-card" style={{ maxHeight: '200px', overflowY: 'auto', padding: '0.5rem', marginTop: '0.5rem', borderRadius: '10px' }}>
                                                {availableUsers.filter(u =>
                                                (u.name?.toLowerCase().includes(searchEmail.toLowerCase()) ||
                                                    u.email?.toLowerCase().includes(searchEmail.toLowerCase()) ||
                                                    u.rollNo?.toLowerCase().includes(searchEmail.toLowerCase()))
                                                ).slice(0, 5).map(u => (
                                                    <div
                                                        key={u.email}
                                                        style={{ padding: '0.5rem', borderRadius: '5px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '0.5rem' }}
                                                        className="hover-bg-glass"
                                                        onClick={() => {
                                                            setSearchEmail(u.email);
                                                            setShowSuggestions(false);
                                                        }}
                                                    >
                                                        <div style={{ width: 24, height: 24, borderRadius: '50%', background: 'var(--primary)', overflow: 'hidden' }}>
                                                            {u.profile_pic ? <img src={u.profile_pic} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} /> : null}
                                                        </div>
                                                        <div style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                                            <div style={{ fontWeight: 'bold', fontSize: '0.85rem' }}>{u.name}</div>
                                                            <div style={{ fontSize: '0.75rem', opacity: 0.6 }}>{u.rollNo}</div>
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                    <button onClick={handleInvite} className="btn-profile btn-profile-secondary">Send Invitation</button>
                                </div>
                            </div>
                        )}

                        {isLead && team.status !== 'locked' && (
                            <div className="glass-card" style={{ padding: '1.5rem', border: '1px solid var(--accent)' }}>
                                <h3 className="profile-card-header" style={{ fontSize: '1.1rem', color: 'var(--accent)' }}><AlertCircle /> Team Lead Panel</h3>
                                <div style={{ marginTop: '1rem' }}>
                                    <button
                                        onClick={handleLock}
                                        className="btn-profile btn-profile-primary"
                                        style={{ width: '100%', marginBottom: '1rem' }}
                                        disabled={members.length < task.max_members}
                                    >
                                        Lock Team 🔒
                                    </button>
                                    {members.length < task.max_members && (
                                        <p style={{ fontSize: '0.8rem', color: 'var(--accent)', textAlign: 'center' }}>
                                            Team must be full ({task.max_members} members) to lock.
                                        </p>
                                    )}

                                    {/* Delete Team Button */}
                                    <button
                                        onClick={handleDeleteTeam}
                                        className="btn-profile"
                                        style={{ width: '100%', background: '#dc3545', color: 'white', marginTop: '1rem' }}
                                    >
                                        <Trash2 size={16} style={{ marginRight: 8 }} />
                                        Delete Team
                                    </button>
                                    <p style={{ fontSize: '0.75rem', marginTop: '0.5rem', opacity: 0.6, textAlign: 'center' }}>
                                        This will permanently delete the team
                                    </p>
                                </div>
                            </div>
                        )}

                        <button onClick={() => navigate(`/formation/${task.id}`)} className="btn-profile btn-profile-secondary">View All Teams</button>
                    </div>

                </div>
            </div>
        </div>
    );
}
